// Copy and presentation rules for the save offer: the one screen that asks a leaving
// studio to stay.
//
// Andrew approved this on 2026-10-01 (card exit-reason-prompt) and three of his words
// decide what is in this file:
//   1. "The offer should appear no matter waht" - nothing here looks at which reason the
//      studio picked. There is no per-reason copy and no per-reason rule.
//   2. "we need to manually add it with a code" - no price, no payment, no Stripe. The
//      screen promises a confirmation email within one business day, which is the same
//      promise upgrades already make, and a person applies the code.
//   3. "idc if its combined with a previous offer" - see SMALL_PRINT below. This is the
//      one place the round 3 copy he approved had to change rather than be copied, and
//      it is named there rather than quietly dropped.
//
// The discount percentage and the renewal date are NOT held here. They are read from
// what the server returns, for the same reason the six exit reasons are fetched rather
// than listed in the front end: the number a studio reads and the number recorded
// against its account must come from one place. A hardcoded 25 here would be exactly
// that drift, so there is not one, not even as a fallback.

export const OFFER_HEADING = (discountPercent) =>
    `Would ${discountPercent} percent off next year change your mind?`;

export const offerBody = ({ discountPercent, renewalDate }) =>
    `Your plan renews on ${renewalDate}. If you would like to stay active, we will take `
    + `${discountPercent} percent off that renewal, one time. Nothing else changes: same `
    + 'plan, same rooms, same classes.';

export const ACCEPT_LABEL = 'Yes, apply the discount';
export const DECLINE_LABEL = 'No thanks, cancel my account';

// The round 3 copy read "...and cannot be combined with another code." Andrew's answer
// was "idc if its combined with a previous offer", so that clause is gone: there is no
// such check anywhere in the server either (see the note at the top of
// server/src/constants/saveOffer.js), and small print that claims a rule we do not
// enforce is a sentence that will one day be used against us by a studio holding the
// MTJGD code. The two parts he did keep, one per account and next renewal only, are
// both really enforced, the first by a unique index.
export const SMALL_PRINT =
    'One discount per account. It applies to your next renewal only. We will confirm by '
    + 'email within one business day.';

export const ACCEPTED_HEADING = 'Thanks for staying.';

export const acceptedBody = (discountPercent) =>
    `We have noted ${discountPercent} percent off your next renewal and you will get a `
    + 'confirmation email within one business day. Nothing else changes in your account.';

// Shown when the answer could not be sent at all and nothing was recorded. The studio
// stays on the offer so a network hiccup cannot cost them the discount by default.
export const RESPOND_FAILED =
    'We could not send that just now and nothing has been saved. Please try again, or '
    + 'email info@studioshuffle.com if it keeps happening.';

// Is there enough here to draw the screen.
//
// Kept as a function so it can be checked without rendering anything, and deliberately
// strict about the two values the body names out loud. A studio must never read "Your
// plan renews on null" or "Would undefined percent off next year change your mind?": if
// either value is missing, there is no offer, and the ordinary closing copy is the right
// screen. The server already answers offered: false whenever it means no, so this is the
// second half of that guard rather than a replacement for it.
export const isOfferDrawable = (offer) => {
    if (!offer || offer.offered !== true) return false;

    const { discountPercent, renewalDate } = offer;

    if (typeof discountPercent !== 'number' || !Number.isFinite(discountPercent)) return false;
    if (discountPercent <= 0) return false;
    if (typeof renewalDate !== 'string' || !renewalDate.trim()) return false;

    return true;
};
